import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import { StatCard, PageCard, BtnPrimary, BtnSecondary, Badge } from '../../components/UI/UIComponents';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

/* ── Icons ── */
const MeetingIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5 2-5 2V10z"/><path d="M4 8h9a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 012-2z"/></svg>;
const UserIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
const LangIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>;
const ClockIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>;
const ArrowRight = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
const PlayIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polygon points="5 3 19 12 5 21 5 3"/></svg>;

const RECENT_MEETINGS = [
  { id: 1, title: 'Product Sync',    host: 'Tanaka-san',   lang: 'JA → EN', time: '10:00 AM', date: 'Today',      status: 'completed', participants: 4 },
  { id: 2, title: 'Client Review',   host: 'Smith, J.',    lang: 'EN → JA', time: '2:30 PM',  date: 'Today',      status: 'completed', participants: 3 },
  { id: 3, title: 'Sprint Planning', host: 'Yamada-san',   lang: 'JA → EN', time: '9:00 AM',  date: 'Yesterday',  status: 'completed', participants: 6 },
  { id: 4, title: 'Design Review',   host: 'Park, M.',     lang: 'EN → JA', time: '4:00 PM',  date: 'Yesterday',  status: 'completed', participants: 2 },
];

const QUICK_STEPS = [
  { step: '1', title: 'Enter your name',       desc: 'Set your display name for the meeting' },
  { step: '2', title: 'Choose language',        desc: 'Select EN ↔ JA interpretation direction' },
  { step: '3', title: 'Create or join',         desc: 'Start a new meeting or join via link' },
  { step: '4', title: 'Start interpretation',  desc: 'Enable speech recognition in meeting' },
];

const API_BASE = process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_PORT || 5001}`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const [stats, setStats] = useState({ totalMeetings: '—', upcoming: '—', recurring: '—', thisWeek: 0 });

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch real stats from Zoom API
  useEffect(() => {
    const email = localStorage.getItem('email') || localStorage.getItem('hostEmail') || 'rukucse11@gmail.com';
    const fetchStats = async () => {
      try {
        const [upRes, pastRes] = await Promise.all([
          fetch(`${API_BASE}/meetings?email=${encodeURIComponent(email)}&type=upcoming&page_size=50`),
          fetch(`${API_BASE}/meetings?email=${encodeURIComponent(email)}&type=previous_meetings&page_size=50`),
        ]);
        const upData = upRes.ok ? await upRes.json() : { meetings: [], total_records: 0 };
        const pastData = pastRes.ok ? await pastRes.json() : { meetings: [], total_records: 0 };
        const allUp = upData.meetings || [];
        const total = (upData.total_records || 0) + (pastData.total_records || 0);
        const recurring = allUp.filter(m => m.type === 3 || m.type === 8).length;
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thisWeek = (pastData.meetings || []).filter(m => m.date && m.date !== '—' && new Date(m.date) >= weekAgo).length;
        setStats({
          totalMeetings: total,
          upcoming: allUp.length,
          recurring,
          thisWeek,
        });
      } catch (err) {
        console.error('Dashboard stats fetch error:', err);
      }
    };
    fetchStats();
  }, []);

  const fmt = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const fmtDate = (d) => d.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

  return (
    <MainLayout pageTitle="Dashboard" pageSubtitle="Real-time AI interpretation overview">
      <div className="dashboard">

        {/* ── Hero Banner ── */}
        <div className="dash-hero">
          <div className="dash-hero-left">
            <p className="dash-hero-date">{fmtDate(time)}</p>
            <h2 className="dash-hero-title">Welcome back! 👋</h2>
            <p className="dash-hero-sub">Your AI interpretation engine is online and ready.</p>
            <div className="dash-hero-actions">
              <BtnPrimary onClick={() => navigate('/create')}>
                <PlayIcon/> Create Meeting
              </BtnPrimary>
              <BtnSecondary onClick={() => navigate('/join')}>
                Join Meeting
              </BtnSecondary>
            </div>
          </div>
          <div className="dash-hero-right">
            <div className="dash-clock">{fmt(time)}</div>
            <div className="dash-status-pill">
              <span className="dash-status-dot"/>
              AI Engine Online
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="stats-grid">
          <StatCard
            title="Total Meetings"
            value={stats.totalMeetings}
            subtitle="All time"
            trend={{ up: true, text: `${stats.thisWeek} this week` }}
            variant="orange"
            icon={<MeetingIcon/>}
          />
          <StatCard
            title="Upcoming Meetings"
            value={stats.upcoming}
            subtitle="Scheduled"
            variant="blue"
            icon={<UserIcon/>}
          />
          <StatCard
            title="Languages Supported"
            value="5"
            subtitle="EN, JA, ZH, VI, BN"
            trend={{ up: true, text: 'More coming soon' }}
            variant="teal"
            icon={<LangIcon/>}
          />
          <StatCard
            title="Recurring Meetings"
            value={stats.recurring}
            subtitle="Active schedules"
            variant="green"
            icon={<ClockIcon/>}
          />
        </div>

        {/* ── Two columns ── */}
        <div className="dash-two-col">

          {/* Recent Meetings */}
          <PageCard
            title="Recent Meetings"
            subtitle="Your last 4 sessions"
            action={
              <button className="dash-see-all" onClick={() => navigate('/history')}>
                View all <ArrowRight/>
              </button>
            }
          >
            <div className="recent-list">
              {RECENT_MEETINGS.map((m) => (
                <div className="recent-item" key={m.id}>
                  <div className="recent-avatar">{m.title[0]}</div>
                  <div className="recent-info">
                    <p className="recent-title">{m.title}</p>
                    <p className="recent-meta">{m.host} · {m.participants} participants</p>
                  </div>
                  <div className="recent-right">
                    <Badge label={m.lang} variant="info"/>
                    <p className="recent-time">{m.date}, {m.time}</p>
                    <Badge label={m.status} variant="success"/>
                  </div>
                </div>
              ))}
            </div>
          </PageCard>

          {/* Quick Start */}
          <PageCard title="Quick Start Guide" subtitle="How it works in 4 steps">
            <div className="quickstart-list">
              {QUICK_STEPS.map((s) => (
                <div className="qs-item" key={s.step}>
                  <div className="qs-step">{s.step}</div>
                  <div>
                    <p className="qs-title">{s.title}</p>
                    <p className="qs-desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{marginTop: 20}}>
              <BtnPrimary onClick={() => navigate('/create')} fullWidth>
                Get Started Now
              </BtnPrimary>
            </div>
          </PageCard>

        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
